import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import SortDirection, { type SortDirectionType, } from "@rio-cloud/rio-uikit/SortDirection";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import TableViewToggles, { type TableViewTogglesViewType, } from "@rio-cloud/rio-uikit/TableViewToggles";
import { SocialUser } from "@types";
import classNames from "classnames";
import omit from "lodash/fp/omit";
import moment from "moment";
import { useState } from "react";

const defaultColumnOrder = [
  "parameter",
  "planned",
  "accomplished",
];

type ColumnLabel = {
  [key: string]: string;
};
const columnLabels: ColumnLabel = {
  parameter: "Parâmetro",
  planned: "Planejado",
  accomplished: "Realizado",
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 260,
    defaultWidth: 260,
    maxWidth: 260,
  },
  // origin: {
  //   width: 200,
  //   defaultWidth: 200,
  //   maxWidth: 200,
  // },
  // destination: {
  //   width: 200,
  //   defaultWidth: 200,
  //   maxWidth: 200,
  // },
  // vehicle: {
  //   width: 260,
  //   defaultWidth: 260,
  //   maxWidth: 260,
  // },
};

const getSortDir = (
  sortDir: SortDirectionType,
  sortBy: string,
  previousSortBy: string
) => {
  if (sortBy === previousSortBy) {
    return sortDir === SortDirection.ASCENDING
      ? SortDirection.DESCENDING
      : SortDirection.ASCENDING;
  }
  return SortDirection.ASCENDING;
};

export type Props = {
  viewType: TableViewTogglesViewType;
  pages: number;
  page: number;
  followers?: Array<SocialUser>;
  usersAmity?: Amity.User[];
  handlePage: (page: number) => void;
  handleSort: (dir: string, newSort: string) => void;
  handleShowTripDetail: (id: string) => void;
  metrics?: Array<{
    parameter: string;
    planned: string;
    accomplished: string;
    icon: string;
    status: string;
  }>;
};

const TableTripsDetails = (props: Props) => {
  const formatNumericValue = (value: string, parameter: string): string => {
    // Aplica formatação específica para parâmetros numéricos
    const numericParameters = [
      'Emissão CO2',
      'Consumo combustível',
      'Distância percorrida',
      'Rendimento combustível',
    ];

    const externOb: ColumnLabel = {
      'Distância percorrida': 'km',
      'Consumo combustível': 'l',
      'Emissão CO2': 'kg CO₂'
    }

    if (!numericParameters.includes(parameter)) {
      return value;
    }

    // Se o valor já contém vírgula, não altera
    if (value.includes(',')) {
      return value;
    }

    // Se o valor é "Indefinido" ou "Indisponível", retorna como está
    if (value === 'Indefinido' || value === 'Indisponível') {
      return 'Sem dados';
    }

    // Extrai o número e a unidade
    const match = value.match(/^([\d.]+)\s*(.*)$/);
    if (!match) {
      return value;
    }

    const [, numberStr, unit] = match;
    const number = parseFloat(numberStr);

    if (isNaN(number)) {
      return value;
    }

    // Formata o número com vírgula e mantém a unidade
    const formattedNumber = number.toFixed(2).replace('.', ',');
    const extern =  externOb[parameter] ? externOb[parameter] : '';
    return unit ? `${formattedNumber} ${unit}` : `${formattedNumber} ${extern}`;
  };

  const { viewType: externalViewType } = props;
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState<SortDirectionType>(
    SortDirection.ASCENDING
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState(
    externalViewType || TableViewToggles.VIEW_TYPE_TABLE
  );

  const tripMetrics = props.metrics ?? [];

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");
    if (newSortBy) {
      handleCardSortChange(newSortBy, getSortDir(sortDir, newSortBy, sortBy));
      props.handleSort(sortDir, newSortBy);
    }
  };

  const handleCardSortChange = (
    newSortBy: string,
    newSortDir: SortDirectionType
  ) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
  };

  // May be extracted as a dedicated component but for demo purpose it's shown here
  const renderTableHead = (
    column: string,
    label: string,
    sortByColumn: string,
    sortDirection: SortDirectionType
  ) => {
    const tableHeadClassNames = classNames("user-select-none", "sort-column");

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
        style={{ borderBottom: "4px solid var(--color-white)" }}
      >
        <span>
          {sortByColumn === column ? (
            <SortArrows direction={sortDirection} />
          ) : (
            <SortArrows />
          )}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  // filter for hidden columns
  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  // filter data to omit hidden columns
  const withoutHidden = omit(hiddenColumns);

  const tableClassNames = classNames(
    "table",
    "table-column-overflow-hidden",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD &&
    "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS &&
    "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy,
      disabled: false,
    };
  });

  return (
    <div id="table-trips">
      {sortBy && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortBy}
          sortOrder={sortDir}
          onSortChange={handleCardSortChange}
        />
      )}
      <div>
        <table className={tableClassNames} style={{ border: "1px solid var(--color-white)" }}>
          <colgroup>
            {columns.map((column) =>
              renderTableCaption(column, columnsDetails[column])
            )}

          </colgroup>
          <thead>
            <tr>
              {columns.map((column) =>
                renderTableHead(column, columnLabels[column], sortBy, sortDir)
              )}

            </tr>
          </thead>
          <tbody>
            
            {tripMetrics.map((row, index: number) => {
              const ruleStatus = (accomplished: string, planned: string, parameter: string) => {
                const parseTimeToMinutes = (timeStr: string): number | null => {
                  const timeMatch = timeStr.match(/(\d+)h(\d+)m/);
                  if (timeMatch) {
                    const hours = parseInt(timeMatch[1], 10);
                    const minutes = parseInt(timeMatch[2], 10);
                    return hours * 60 + minutes;
                  }
                  return null;
                };

                const parseValue = (value: string): number | null => {
                  const timeInMinutes = parseTimeToMinutes(value);
                  if (timeInMinutes !== null) {
                    return timeInMinutes;
                  }
                  
                  const numValue = parseFloat(value.replace(',', '.'));
                  return isNaN(numValue) ? null : numValue;
                };

                const isRouteDeviation = parameter === "Desvio de rota" || parameter === "Route deviation" || parameter === "Desvios de rota";
                
                if (isRouteDeviation) {
                  const plannedLower = planned.toLowerCase();
                  const accomplishedLower = accomplished.toLowerCase();
                  
                  const isPlannedYes = plannedLower === "sim" || plannedLower === "yes";
                  const isPlannedNo = plannedLower === "não" || plannedLower === "no" || plannedLower === "nao";
                  const isAccomplishedYes = accomplishedLower === "sim" || accomplishedLower === "yes";
                  const isAccomplishedNo = accomplishedLower === "não" || accomplishedLower === "no" || accomplishedLower === "nao";
                  
                  if (isPlannedNo && isAccomplishedNo) {
                    return "text-color-success";
                  }
                  
                  if (isPlannedNo && isAccomplishedYes) {
                    return "text-color-danger";
                  }
                  
                  if (isPlannedYes && isAccomplishedYes) {
                    return "text-color-success";
                  }
                  
                  if (isPlannedYes && isAccomplishedNo) {
                    return "text-color-danger";
                  }
                  
                  return "text-color-gray";
                }

                const accomplishedNum = parseValue(accomplished);
                const plannedNum = parseValue(planned);
                
                if (accomplishedNum === null || plannedNum === null || !planned) {
                  return "text-color-gray"
                }
                
                if (accomplishedNum > plannedNum) {
                  return "text-color-danger"
                } else {
                  return "text-color-success"
                }
              }

              return (
                <tr key={index} style={{ border: "4px solid var(--color-black)", background: "var(--color-highlight-lightest)" }}>
                  <td key={"Parâmetro"} data-field={"Parâmetro"}
                    style={{ borderBottom: "4px solid var(--color-white)", borderTop: "4px solid var(--color-white)" }}
                  >
                    <span className={`${row.icon} margin-right-10 text-color-black text-size-16`}></span>
                    <span className="text-color-black">{row.parameter}</span>
                  </td>
                  <td key={"Planejado"} data-field={"Planejado"} style={{ borderBottom: "4px solid var(--color-white)", borderTop: "4px solid var(--color-white)" }}>
                    <span className="text-color-black">{formatNumericValue(row.planned, row.parameter)}</span>
                  </td>
                  <td key={"Realizado"} data-field={"Realizado"} style={{ borderBottom: "4px solid var(--color-white)", borderTop: "4px solid var(--color-white)" }}>
                    <span className={`rioglyph rioglyph-sphere ${ruleStatus(row.accomplished, row.planned, row.parameter)}`}></span>
                    <span className="margin-left-10 text-color-black">{formatNumericValue(row.accomplished, row.parameter)}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div >
  );

}

export default TableTripsDetails;